import { X, Plane, Clock } from 'lucide-react';

/**
 * FlightDetailsModal
 * 
 * Shows detailed OOOI times for:
 * - Inbound flight (previous leg)
 * - Current flight (OUT/OFF/ON/IN times + ETA)
 */
const FlightDetailsModal = ({ 
  currentFlight, 
  previousFlight,
  onClose,
  show 
}) => {
  if (!show) return null;
  
  // Format time from different time object formats
  const formatTime = (timeObj) => {
    if (!timeObj) return '—';
    
    let dateStr;
    if (timeObj.local_wall) {
      dateStr = timeObj.local_wall;
    } else if (timeObj.utc) {
      dateStr = timeObj.utc;
    } else if (typeof timeObj === 'string') {
      dateStr = timeObj;
    } else {
      return '—';
    }
    
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } catch (err) {
      return '—';
    }
  };
  
  // Calculate ETA for current flight
  const calculateETA = () => {
    if (!currentFlight) return null;
    
    const leg = currentFlight;
    const getTime = (timeObj) => {
      if (!timeObj) return null;
      if (timeObj.utc) return new Date(timeObj.utc);
      if (typeof timeObj === 'string') return new Date(timeObj);
      return null;
    };
    
    // If we have actual in time, use it
    const actualIn = getTime(leg.actual_in);
    if (actualIn) return formatTime(leg.actual_in);
    
    // Otherwise estimate based on scheduled arrival + delay
    const schedOut = getTime(leg.sched_out);
    const actualOut = getTime(leg.actual_out);
    const schedIn = getTime(leg.sched_in);
    
    if (!schedOut || !actualOut || !schedIn) {
      return formatTime(leg.sched_in) + ' (scheduled)';
    }
    
    const delayMs = actualOut - schedOut;
    const estimatedIn = new Date(schedIn.getTime() + delayMs);
    
    return estimatedIn.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    }) + ' (estimated)';
  };
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-lg mx-auto">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Flight Details
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 space-y-6">
            {/* Inbound Flight */}
            {previousFlight && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Plane className="w-4 h-4 rotate-90" />
                  <h3 className="font-medium text-sm uppercase">Inbound Flight</h3>
                </div>
                
                <div className="pl-6 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Flight:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {previousFlight.flight_number || '—'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">From:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {previousFlight.departure || '—'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Arrived:</span>
                    <span className="font-mono text-slate-900 dark:text-white">
                      {formatTime(previousFlight.actual_in || previousFlight.sched_in)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Gate:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {previousFlight.arrival_gate || '—'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Divider */}
            {previousFlight && currentFlight && (
              <div className="border-t border-slate-200 dark:border-slate-700" />
            )}
            
            {/* Current Flight */}
            {currentFlight && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <Plane className="w-4 h-4" />
                  <h3 className="font-medium text-sm uppercase">Current Flight</h3>
                </div>
                
                <div className="pl-6 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Flight:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {currentFlight.flight_number || '—'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Route:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {currentFlight.departure || '—'} → {currentFlight.arrival || '—'}
                    </span>
                  </div>
                  
                  <div className="mt-4 space-y-2 bg-slate-50 dark:bg-slate-900/50 rounded p-3">
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-400 uppercase">
                      <Clock className="w-3 h-3" />
                      <span>OOOI Times</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-slate-500 dark:text-slate-400 text-xs">OUT (Pushback)</div>
                        <div className="font-mono text-slate-900 dark:text-white">
                          {formatTime(currentFlight.actual_out || currentFlight.sched_out)}
                        </div>
                      </div>
                      
                      <div>
                        <div className="text-slate-500 dark:text-slate-400 text-xs">OFF (Wheels Up)</div>
                        <div className="font-mono text-slate-900 dark:text-white">
                          {formatTime(currentFlight.actual_out || currentFlight.sched_out)}
                        </div>
                      </div>
                      
                      <div>
                        <div className="text-slate-500 dark:text-slate-400 text-xs">ON (Wheels Down)</div>
                        <div className="font-mono text-slate-900 dark:text-white">
                          {formatTime(currentFlight.actual_in || currentFlight.sched_in)}
                        </div>
                      </div>
                      
                      <div>
                        <div className="text-slate-500 dark:text-slate-400 text-xs">IN (Gate Arrival)</div>
                        <div className="font-mono text-slate-900 dark:text-white">
                          {calculateETA()}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {currentFlight.aircraft_registration && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">Aircraft:</span>
                      <span className="font-mono text-slate-900 dark:text-white">
                        {currentFlight.aircraft_registration}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={onClose}
              className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FlightDetailsModal;
